import React, {
  useState,
  useEffect,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Card, Row, Col, Form, Button, Badge } from "react-bootstrap";
import { ESCALATION_FIELDS_CONFIG } from "../config/escalationConfig";
import { getAdditionalConditionFields } from "../utils/escalationFields";
import { startCase } from "lodash";
import FormikSelectField from "../../../../shared/components/FormikSelectField";
import { useDispatch, useSelector } from "react-redux";
import { loadDropdownCacheRequest } from "../redux/Actions";

const ConditionBuilder = forwardRef(({ isEdit, initialData }, ref) => {
  const dispatch = useDispatch();
  const dropdownCache = useSelector((state) => state.dropdown.cache); // Read dropdown cache from Redux

  const conditionGroups = useMemo(
    () => getAdditionalConditionFields(ESCALATION_FIELDS_CONFIG),
    []
  );

  const defaultGroupName = conditionGroups[0]?.groupName || "";
  const defaultSubField = conditionGroups[0]?.fields?.[0]?.label || "";
  // Builder visibility (keep toggle/button in same top-right location)
  const [showBuilder, setShowBuilder] = useState(false);

  // start with NO groups (user sees "No groups configured..." initially)
  const [groups, setGroups] = useState([]);

  // connectors between groups
  const [connectors, setConnectors] = useState([]);

  // errors & touched state keyed by `${groupId}-${condId}-${key}`
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const isNullOperator = (op) =>
    op?.toLowerCase() === "is_null" || op?.toLowerCase() === "is_not_null";

  const isMultiOperator = (op) =>
    op?.toLowerCase() === "in" || op?.toLowerCase() === "not_in";

  const getOptionValue = (opt) => opt.id ?? opt.value;



  useEffect(() => {
    if (isEdit || groups.length != 0) {
      setShowBuilder(true);
    }
    const expected = Math.max(0, groups.length - 1);
    setConnectors((prev) => {
      if (prev.length === expected) return prev;
      if (prev.length < expected)
        return [...prev, ...Array(expected - prev.length).fill("AND")];
      return prev.slice(0, expected);
    });
  }, [groups]);

  useEffect(() => {
    if (!isEdit || !initialData) return;

    // Prevent running multiple times
    if (editInitialized.current) return;
    editInitialized.current = true;

    const editGroups = initialData.groups || [];

    const mapped = editGroups.map((g, index) => ({
      id: index + 1,
      logic: g.internalOperator || "AND",
      color: ["primary", "purple", "success", "orange"][index % 4],
      conditions: g.conditions.map((c, cIndex) => {
        const matchGroup =
          conditionGroups.find((gr) =>
            gr.fields.some((f) => f.value === c.field_code)
          ) || conditionGroups[0];

        const matchField =
          matchGroup.fields.find((f) => f.value === c.field_code) ||
          matchGroup.fields[0];

        let initialValue = c.value;
        let initialLabel = c.valueLabel;

        // MULTI-SELECT MAPPING
        if (Array.isArray(c.value)) {
          if (matchField?.staticOptions?.length > 0) {
            initialLabel = c.value.map((v) => {
              const found = matchField.staticOptions.find(
                (opt) => String(opt.value) === String(v)
              );
              return found ? found.label : String(v);
            });
          } else if (matchField?.dataSource === "api") {
            const apiOpts = dropdownCache[matchField.apiEndpoint] || [];
            initialLabel = c.value.map((v) => {
              const found = apiOpts.find(
                (opt) => String(opt.value) === String(v)
              );
              return found ? found.label : String(v);
            });
          }
        }

        return {
          id: cIndex + 1,
          fieldType: matchGroup.groupName,
          subField: matchField.label,
          field_code: matchField.value,
          operator: c.operator.toLowerCase(),
          value: initialValue,
          selectedLabel: initialLabel,
          apiOptions: dropdownCache[matchField.apiEndpoint] || [],
        };
      }),
    }));

    setGroups(mapped);

    const nextOps = editGroups.map((g) => g.nextOperator).filter(Boolean);
    setConnectors(nextOps);
  }, [isEdit, initialData]);

  const handleToggle = () => setShowBuilder((s) => !s);

  useImperativeHandle(ref, () => ({
    validateAndGetData: () => {
      let isValid = true;
      const newErrors = {};

      groups.forEach((group) => {
        group.conditions.forEach((condition) => {
          if (!condition.fieldType) {
            isValid = false;
            newErrors[`${group.id}-${condition.id}-fieldType`] =
              "Field type is required";
          }
          if (!condition.subField) {
            isValid = false;
            newErrors[`${group.id}-${condition.id}-subField`] =
              "Field is required";
          }
          if (!condition.operator) {
            isValid = false;
            newErrors[`${group.id}-${condition.id}-operator`] =
              "Operator is required";
          }

          // Skip validation when operator is is_null / is_not_null
          if (!isNullOperator(condition.operator)) {
            if (
              condition.value === undefined ||
              condition.value === null ||
              String(condition.value).trim() === ""
            ) {
              isValid = false;
              newErrors[`${group.id}-${condition.id}-value`] =
                "Value is required";
            }
          }
        });
      });

      setErrors(newErrors);

      if (!isValid) return { isValid: false };

      // Build formatted output
      const formattedGroups = groups.map((group, idx) => {
        const nextOperator = idx < connectors.length ? connectors[idx] : null;

        const formattedConditions = group.conditions.map((c) => {
          let finalLabel = c.selectedLabel;

          // Detect matching field definition
          const cg = conditionGroups.find(
            (g) => g.groupName === c.fieldType
          ) || { fields: [] };
          const sf = cg.fields?.find((f) => f.label === c.subField);

          // Multi-select case: value is an array
          if (Array.isArray(c.value)) {
            finalLabel = [];

            // Static options mapping
            if (sf?.staticOptions?.length > 0) {
              c.value.forEach((v) => {
                const match = sf.staticOptions.find(
                  (opt) => String(opt.value) === String(v)
                );
                finalLabel.push(match ? match.label : String(v));
              });
            }

            // API options mapping
            else if (sf?.dataSource === "api") {
              const apiOpts = dropdownCache[sf.apiEndpoint] || [];
              c.value.forEach((v) => {
                const match = apiOpts.find(
                  (opt) => String(opt.value) === String(v)
                );
                finalLabel.push(match ? match.label : String(v));
              });
            }
          }

          // Single value case
          if (!Array.isArray(c.value)) {
            finalLabel = c.selectedLabel || c.valueLabel || String(c.value);
          }

          return {
            field_code: c.field_code,
            field_name: c.subField || "",
            operator: (c.operator || "").toLowerCase(),
            dataType: getDataType(c.value),
            value: c.value,
            valueLabel: finalLabel,
          };
        });

        return {
          internalOperator: group.logic,
          conditions: formattedConditions,
          nextOperator,
        };
      });

      return {
        isValid: true,
        additional_conditions: { groups: formattedGroups },
      };
    },
  }));

  const getDataType = (val) => {
    if (val === true || val === false || val === "true" || val === "false") {
      return "BOOLEAN";
    }
    // ARRAY (STRING or NUMBER)
    if (Array.isArray(val)) {
      const allNumbers = val.every(
        (v) => typeof v === "number" || /^[+-]?\d+(\.\d+)?$/.test(String(v))
      );
      return allNumbers ? "NUMBER" : "STRING";
    }
    if (typeof val === "number" || /^[+-]?\d+(\.\d+)?$/.test(String(val))) {
      return "NUMBER";
    }
    if (typeof val === "string") {
      // DATETIME formats
      const isDateTime =
        /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(val) ||   // 2025-01-10T15:20
        /\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(val);     // 2025-01-10 15:20

      if (isDateTime) return "DATETIME";

      // DATE format
      const isDate = /^\d{4}-\d{2}-\d{2}$/.test(val); // 2025-01-10 only date

      if (isDate) return "DATE";
    }
    // Default: STRING
    return "STRING";
  };

  const markTouched = (groupId, condId, key) => {
    setTouched((prev) => ({ ...prev, [`${groupId}-${condId}-${key}`]: true }));
  };

  const setError = (groupId, condId, key, msg) => {
    setErrors((prev) => ({ ...prev, [`${groupId}-${condId}-${key}`]: msg }));
  };

  const clearError = (groupId, condId, key) => {
    setErrors((prev) => {
      const copy = { ...prev };
      delete copy[`${groupId}-${condId}-${key}`];
      return copy;
    });
  };

  // create a new group with 1 condition and attempt to auto-fill defaults
  const handleAddGroup = async () => {
    const newId = groups.length + 1;
    const colors = ["primary", "purple", "success", "orange"];
    const newColor = colors[(newId - 1) % colors.length];
    setShowBuilder(true);

    // choose first conditionGroup from config or empty
    const startFieldType = defaultGroupName;
    const startSubField = defaultSubField;

    // find selectedField metadata
    const cg = conditionGroups.find((c) => c.groupName === startFieldType) ||
      conditionGroups[0] || { fields: [] };
    const subFieldMeta =
      cg.fields?.find((f) => f.label === startSubField) ||
      cg.fields?.[0] ||
      null;

    // default operator: first operator if available
    const defaultOperator = subFieldMeta?.operators?.[0] || "Equals";

    // prepare condition object; value might be auto-filled below
    const newCondition = {
      id: 1,
      fieldType: startFieldType,
      subField: startSubField,
      operator: defaultOperator,
      field_code: subFieldMeta?.value || "",
      value: "",
      apiOptions: [],
      selectedLabel: "",
    };

    if (subFieldMeta?.type === "boolean") {
      newCondition.value = "true";
      newCondition.selectedLabel = "Yes";
    }
    // if API-backed, try preloading and pick first option
    if (
      subFieldMeta?.type === "select" &&
      subFieldMeta?.dataSource === "api" &&
      subFieldMeta?.apiEndpoint
    ) {
      const endpoint = subFieldMeta.apiEndpoint;

      //  If data already in Redux cache → use it
      // if (dropdownCache[endpoint]) {
      //   newCondition.apiOptions = dropdownCache[endpoint];
      // } else {
      //   // Trigger Redux Saga to fetch data
      //   dispatch(loadDropdownCacheRequest(endpoint));
      //   // Temporarily keep empty options until saga fills cache
      //   newCondition.apiOptions = [{ label: "Loading...", value: "" }];
      // }


      newCondition.apiOptions = apiOptionsCache.current.get(endpoint);
    }
    // compose group
    const newGroup = {
      id: newId,
      logic: "AND",
      color: newColor,
      conditions: [newCondition],
    };

    setGroups((prev) => [...prev, newGroup]);
  };

  const handleAddCondition = async (groupId) => {
    setGroups((prev) =>
      prev.map((group) => {
        if (group.id !== groupId) return group;
        const newCondId = group.conditions.length + 1;
        const firstFieldType = defaultGroupName;
        const firstSubField = defaultSubField;
        const cg = conditionGroups.find(
          (c) => c.groupName === firstFieldType
        ) ||
          conditionGroups[0] || { fields: [] };
        const sf =
          cg.fields?.find((f) => f.label === firstSubField) ||
          cg.fields?.[0] ||
          null;
        const defaultOperator = sf?.operators?.[0] || "Equals";
        const cond = {
          id: newCondId,
          fieldType: firstFieldType,
          subField: firstSubField,
          field_code: sf?.value || "",
          operator: defaultOperator,
          value: "",
          apiOptions: [],
          selectedLabel: "",
        };
        // Set default value for boolean fields
        if (sf?.type === "boolean") {
          cond.value = "true";
          cond.selectedLabel = "Yes";
        }
        // attempt static fill (api handled after setGroups because async)
        // if (
        //   sf?.type === "select" &&
        //   sf?.dataSource === "static" &&
        //   (sf.staticOptions?.length ?? 0) > 0
        // ) {
        //   cond.value = sf.staticOptions[0].value;
        // }
        return { ...group, conditions: [...group.conditions, cond] };
      })
    );
  };

  const handleRemoveCondition = (groupId, condId) => {
    setGroups((prev) =>
      prev.map((group) => {
        if (group.id !== groupId) return group;
        if (group.conditions.length <= 1) return group;
        const remaining = group.conditions.filter(
          (condition) => condition.id !== condId
        );
        const reindexed = remaining.map((condition, i) => ({
          ...condition,
          id: i + 1,
        }));
        return { ...group, conditions: reindexed };
      })
    );
    // clear errors/touched related to that condition
    setErrors((prev) => {
      const copy = { ...prev };
      Object.keys(copy).forEach((key) => {
        if (key.startsWith(`${groupId}-${condId}-`)) delete copy[key];
      });
      return copy;
    });
    setTouched((prev) => {
      const copy = { ...prev };
      Object.keys(copy).forEach((key) => {
        if (key.startsWith(`${groupId}-${condId}-`)) delete copy[key];
      });
      return copy;
    });
  };

  const handleRemoveGroup = (groupId) => {
    const idx = groups.findIndex((group) => group.id === groupId);
    if (idx === -1) return;
    const remaining = groups.filter((group) => group.id !== groupId);
    const reindexedGroups = remaining.map((group, i) => ({
      ...group,
      id: i + 1,
    }));
    setGroups(reindexedGroups);

    if (reindexedGroups.length === 0) {
      setConnectors([]);
    }

    // clear errors/touched for that group
    setErrors((prev) => {
      const copy = { ...prev };
      Object.keys(copy).forEach((key) => {
        if (key.startsWith(`${groupId}-`)) delete copy[key];
      });
      return copy;
    });
    setTouched((prev) => {
      const copy = { ...prev };
      Object.keys(copy).forEach((key) => {
        if (key.startsWith(`${groupId}-`)) delete copy[key];
      });
      return copy;
    });
  };

  // Update a specific condition field value
  const handleConditionChange = async (
    groupId,
    conditionId,
    key,
    value,
    selectedLabel
  ) => {
    // mark touched on change for validation behavior
    markTouched(groupId, conditionId, key);

    setGroups((prev) =>
      prev.map((group) =>
        group.id === groupId
          ? {
            ...group,
            conditions: group.conditions.map((condition) => {
              if (condition.id !== conditionId) return condition;

              // fieldType change => reset subField/operator/value and prefill defaults
              if (key === "fieldType") {
                const grp = conditionGroups.find(
                  (cg) => cg.groupName === value
                ) ||
                  conditionGroups[0] || { fields: [] };
                const firstField = grp?.fields?.[0] || null;
                const firstLabel = firstField?.label || "";
                const firstOperator = firstField?.operators?.[0] || "Equals";

                const newCondition = {
                  ...condition,
                  fieldType: value,
                  subField: firstLabel,
                  field_code: firstField?.value || "",
                  operator: firstOperator,
                  value: "",
                  apiOptions: [],
                  selectedLabel: "",
                };
                if (
                  firstField?.type === "select" &&
                  firstField?.dataSource === "api" &&
                  firstField?.apiEndpoint
                ) {
                  (async () => {
                    const opts = apiOptionsCache.current.get(
                      firstField.apiEndpoint
                    );
                    setGroups((pp) =>
                      pp.map((pg) =>
                        pg.id === groupId
                          ? {
                            ...pg,
                            conditions: pg.conditions.map((cc) =>
                              cc.id === conditionId
                                ? { ...cc, apiOptions: opts, value: "" }
                                : cc
                            ),
                          }
                          : pg
                      )
                    );
                  })();
                }
                return newCondition;
              }

              // subField change => reset value and maybe auto-select first operator/value
              if (key === "subField") {
                const groupMeta = conditionGroups.find(
                  (cg) => cg.groupName === condition.fieldType
                ) ||
                  conditionGroups[0] || { fields: [] };
                const sf =
                  groupMeta.fields?.find((f) => f.label === value) || null;
                const firstOperator = sf?.operators?.[0] || "Equals";
                const newCondition = {
                  ...condition,
                  subField: value,
                  operator: firstOperator,
                  value: "",
                  apiOptions: [],
                  field_code: sf?.value || "",
                  selectedLabel: "",
                };
                if (sf?.type === "boolean") {
                  newCondition.value = "true";
                  newCondition.selectedLabel = "Yes";
                }
                if (
                  sf?.type === "select" &&
                  sf?.dataSource === "api" &&
                  sf?.apiEndpoint
                ) {
                  const endpoint = sf.apiEndpoint;
                  (async () => {
                    const opts = apiOptionsCache.current.get(endpoint);
                    setGroups((pp) =>
                      pp.map((pg) =>
                        pg.id === groupId
                          ? {
                            ...pg,
                            conditions: pg.conditions.map((cc) =>
                              cc.id === conditionId
                                ? { ...cc, apiOptions: opts, value: "" }
                                : cc
                            ),
                          }
                          : pg
                      )
                    );
                  })();
                }

                return newCondition;
              }

              // Handle operator-specific behavior (is_null, is_not_null)
              if (key === "operator") {

                const isNullOp = isNullOperator(value);

                // If operator becomes IS NULL / IS NOT NULL → clear value & label
                if (isNullOp) {
                  clearError(groupId, conditionId, "value");
                  // console.log(
                  //   "Condition Updated",
                  //   {
                  //     groupId,
                  //     conditionId,
                  //     key,
                  //     value,
                  //     selectedLabel,
                  //     conditionBefore: condition,
                  //   }
                  // );

                  return {
                    ...condition,
                    operator: value,
                    value: "",
                    selectedLabel: "",
                  };
                }

                // If switching from IS NULL to a normal operator → DO NOT wipe selectedLabel
                return {
                  ...condition,
                  operator: value,
                };
              }


              // operator or value change
              let finalValue = value;
              let finalLabel = selectedLabel;

              // Normalize multi-select values
              if (key === "value" && isMultiOperator(condition.operator)) {

                finalValue = Array.isArray(value) ? value : [value];

                // Find field metadata
                const groupMeta = conditionGroups.find(
                  (cg) => cg.groupName === condition.fieldType
                ) || { fields: [] };

                const sf = groupMeta.fields.find((f) => f.label === condition.subField);

                let labels = [];

                // STATIC OPTIONS
                if (sf?.staticOptions?.length > 0) {
                  labels = finalValue.map((v) => {
                    const match = sf.staticOptions.find(
                      (opt) => String(opt.value) === String(v)
                    );
                    return match ? match.label : String(v);
                  });
                }

                // API OPTIONS
                else if (sf?.dataSource === "api") {
                  const apiOpts = apiOptionsCache.current.get(sf.apiEndpoint) || [];
                  labels = finalValue.map((v) => {
                    const match = apiOpts.find(
                      (opt) => String(opt.value) === String(v)
                    );
                    return match ? match.label : String(v);
                  });
                }

                finalLabel = labels;

                const updated = {
                  ...condition,
                  value: finalValue,
                  selectedLabel: finalLabel,   // ✔ FIXED
                };

                return updated;
              }


              const updated = {
                ...condition,
                [key]: finalValue,
                selectedLabel: finalLabel,
              };
              // clear/set error for value as needed
              if (key === "value") {
                if (!value)
                  setError(
                    groupId,
                    conditionId,
                    "value",
                    "Value is required"
                  );
                else clearError(groupId, conditionId, "value");
              }
              return updated;
            }),
          }
          : group
      )
    );
  };

  const handleGroupLogicChange = (groupId, value) => {
    setGroups((prev) =>
      prev.map((group) =>
        group.id === groupId ? { ...group, logic: value } : group
      )
    );
  };

  const handleConnectorChange = (index, value) => {
    setConnectors((prev) =>
      prev.map((connector, i) => (i === index ? value : connector))
    );
  };

  const editInitialized = React.useRef(false);

  // at the top of ConditionBuilder component body
  const apiOptionsCache = React.useRef(new Map()); // NEW: endpoint -> [{label,value}]
  const [, forceRender] = React.useState(0); // NEW: bump to re-render after cache fill

  // collect all API endpoints currently needed by visible conditions
  const apiEndpointsNeeded = React.useMemo(() => {
    // NEW
    const set = new Set();
    groups.forEach((g) => {
      g.conditions.forEach((c) => {
        const cg = conditionGroups.find((x) => x.groupName === c.fieldType) || {
          fields: [],
        };
        const sf = cg.fields?.find((f) => f.label === c.subField);
        if (
          sf?.type === "select" &&
          sf?.dataSource === "api" &&
          sf.apiEndpoint
        ) {
          set.add(sf.apiEndpoint);
        }
      });
    });
    return Array.from(set);
  }, [groups, conditionGroups]);

  useEffect(() => {
    for (const endpoint of apiEndpointsNeeded) {
      // If Redux cache has data, copy to local cache for UI
      if (dropdownCache[endpoint]) {
        apiOptionsCache.current.set(endpoint, dropdownCache[endpoint]);
        continue;
      }
      // No cache → request via Redux Saga
      dispatch(loadDropdownCacheRequest(endpoint));
    }

    // force render so UI updates with cached data
    forceRender((x) => x + 1);
  }, [apiEndpointsNeeded, dropdownCache]);

  const getGroupSummary = (group) => {
    if (!group.conditions || group.conditions.length === 0) return "empty";

    return group.conditions
      .map((condition) => {
        const sub = condition.subField;
        const op = condition.operator;

        let val = "";

        if (Array.isArray(condition.selectedLabel)) {
          val = condition.selectedLabel.join(", ");
        } else {
          val = condition.selectedLabel || condition.value || "";
        }
        // console.log(
        //   "Summary Row",
        //   {
        //     subField: condition.subField,
        //     operator: condition.operator,
        //     value: condition.value,
        //     selectedLabel: condition.selectedLabel,
        //   }
        // );
        return `${sub} ${op} ${val}`;
      })
      .join(group.logic === "AND" ? " AND " : " OR ");
  };


  // helper to render field-level error by key
  const renderError = (gId, cId, key) => {
    const mapKey = `${gId}-${cId}-${key}`;
    if (
      (!touched[mapKey] && errors[mapKey]) ||
      (touched[mapKey] && errors[mapKey])
    ) {
      return <div className="text-danger small mt-1">{errors[mapKey]}</div>;
    }
    return null;
  };

  return (
    // <div className="escalation-page">
    <Card className="p-4 shadow-sm mt-4">
      <Card.Header>Additional Conditions</Card.Header>
      <Card.Body>
        {/* Header: toggle + Add Group are top-right (same place as original) */}
        {/* <div className="justify-content-between align-items-center mb-2"> */}
          <div className="d-flex justify-content-end">
            <Button
              variant="secondary"
              size="sm"
              className="me-2"
              onClick={handleToggle}
            >
              <i
                className={`fa fa-caret-${showBuilder ? "up" : "down"} me-1`}
              />
              Condition Builder
            </Button>
            <Button
              className="default-btn-color"
              size="sm"
              onClick={handleAddGroup}
            >
              + Add Group
            </Button>
          </div>
        {/* </div> */}

        <p className="text-muted small mb-3">
          Add dynamic filters with AND/OR logic between condition groups
        </p>

        {/* Active Conditions Summary */}
        {groups.length > 0 && (
          <div className="active-conditions mb-3 p-2 border rounded bg-light">
            <span className="fw-semibold">Active Conditions:</span>
            {groups.map((group, idx) => (
              <React.Fragment key={group.id}>
                <Badge
                  bg="light"
                  className={`ms-2 px-2 py-2 border border-${group.color} text-${group.color} fw-normal`}
                >
                  {`Group ${group.id}: ${getGroupSummary(group)}`}
                </Badge>
                {idx < connectors.length && (
                  <span className="connector-pill ms-2 me-2">
                    {connectors[idx]}
                  </span>
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Builder */}
        {showBuilder ? (
          groups.length === 0 ? (
            <div className="text-center text-muted py-4 border rounded bg-light">
              No groups configured. Click <b>“+ Add Group”</b> to define
              additional conditions.
            </div>
          ) : (
            groups.map((group, idx) => (
              <div key={group.id} className="mb-4">
                {idx > 0 && (
                  <div className="text-center mb-3 connector-container">
                    <Form.Select
                      size="sm"
                      className="logic-dropdown"
                      value={connectors[idx - 1] || "AND"}
                      onChange={(e) =>
                        handleConnectorChange(idx - 1, e.target.value)
                      }
                    >
                      <option value="AND">AND</option>
                      <option value="OR">OR</option>
                    </Form.Select>
                  </div>
                )}
                {/* <div className="escalation-page"> */}
                <Card
                  className={`condition-group border-${group.color} rounded-3 escalation-card`}
                >
                  <Card.Header className="bg-transparent border-0 pb-0 d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center ">
                      <span
                        className={`group-badge bg-${group.color} text-white me-2`}
                      >
                        Group {group.id}
                      </span>
                      <Form.Select
                        size="sm"
                        value={group.logic}
                        className="w-auto me-2"
                        onChange={(e) =>
                          handleGroupLogicChange(group.id, e.target.value)
                        }
                      >
                        <option value="AND">AND</option>
                        <option value="OR">OR</option>
                      </Form.Select>
                      <small className="text-muted">
                        {group.logic === "AND"
                          ? "All conditions must match"
                          : "Any condition can match"}
                      </small>
                    </div>

                    <div>
                      <Button
                        className="default-btn-color"
                        size="sm"
                        onClick={() => handleAddCondition(group.id)}
                      >
                        + Add Condition
                      </Button>
                      <Button
                        variant="light"
                        size="sm"
                        className="ms-2 text-danger"
                        onClick={() => handleRemoveGroup(group.id)}
                      >
                        🗑️
                      </Button>
                    </div>
                  </Card.Header>

                  <Card.Body>
                    {group.conditions.map((condition) => {
                      const selectedGroup = conditionGroups.find(
                        (cg) => cg.groupName === condition.fieldType
                      ) ||
                        conditionGroups[0] || { fields: [] };
                      const subFieldOptions = selectedGroup.fields || [];
                      const selectedField =
                        subFieldOptions.find(
                          (f) => f.label === condition.subField
                        ) || null;

                      const showStaticDropdown =
                        selectedField?.type === "select" &&
                        selectedField?.dataSource === "static" &&
                        (selectedField?.staticOptions?.length ?? 0) > 0;
                      const showApiDropdown =
                        selectedField?.type === "select" &&
                        selectedField?.dataSource === "api" &&
                        selectedField?.apiEndpoint;
                          const apiOptions = apiOptionsCache.current.get(selectedField.apiEndpoint) || [];
                        if (selectedField?.apiEndpoint === "/company") console.log("company opts", apiOptions[0]);

                      return (
                        <Row key={condition.id} className="g-2 mb-2">
                          <Col
                            xs="auto"
                            className="col-auto align-items-center mt-3 "
                          >
                            <span className="badge bg-secondary">
                              {condition.id}
                            </span>
                          </Col>

                          <Col md={3}>
                            <FormikSelectField
                              name={`fieldType-${group.id}-${condition.id}`}
                              value={condition.fieldType}
                              onChange={(_, val) =>
                                handleConditionChange(
                                  group.id,
                                  condition.id,
                                  "fieldType",
                                  val
                                )
                              }
                              placeholder="Select Field Type"
                            >
                              {conditionGroups.map((cg, i2) => (
                                <option
                                  key={`${cg.groupName}-${i2}`}
                                  value={cg.groupName}
                                >
                                  {cg.groupName}
                                </option>
                              ))}
                            </FormikSelectField>
                            {renderError(group.id, condition.id, "fieldType")}
                          </Col>

                          <Col md={3}>
                            <FormikSelectField
                              name={`subField-${group.id}-${condition.id}`}
                              value={condition.subField}
                              onChange={(_, val) =>
                                handleConditionChange(
                                  group.id,
                                  condition.id,
                                  "subField",
                                  val
                                )
                              }
                              placeholder="Select Field"
                            >
                              {subFieldOptions.map((f, i2) => (
                                <option
                                  key={`${f.label}-${i2}`}
                                  value={f.label}
                                >
                                  {f.label}
                                </option>
                              ))}
                            </FormikSelectField>
                            {renderError(group.id, condition.id, "subField")}
                          </Col>

                          <Col md={2}>
                            <FormikSelectField
                              name={`operator-${group.id}-${condition.id}`}
                              value={condition.operator?.toLowerCase() || ""}
                              onChange={(_, val) =>
                                handleConditionChange(
                                  group.id,
                                  condition.id,
                                  "operator",
                                  val
                                )
                              }
                              placeholder="Select Operator"
                            >
                              {selectedField?.operators?.map((op, i2) => (
                                <option key={`${op}-${i2}`} value={op}>
                                  {startCase(op)}
                                </option>
                              ))}
                            </FormikSelectField>
                            {renderError(group.id, condition.id, "operator")}
                          </Col>

                          <Col md={3}>
                            {showStaticDropdown ? (
                              <>
                                <FormikSelectField
                                  name={`val-${group.id}-${condition.id}`}
                                  value={condition.value}
                                  disabled={isNullOperator(condition.operator)}
                                  isMulti={isMultiOperator(condition.operator)}
                                  onChange={(name, value, selectedLabel) => {
                                    handleConditionChange(
                                      group.id,
                                      condition.id,
                                      "value",
                                      value,
                                      selectedLabel
                                    );
                                  }}
                                >
                                  {selectedField.staticOptions.map(
                                    (option, index) => (
                                      <option key={index} value={option.value}>
                                        {option.label}
                                      </option>
                                    )
                                  )}
                                </FormikSelectField>
                                {!isNullOperator(condition.operator) &&
                                  renderError(group.id, condition.id, "value")}
                              </>
                            ) : showApiDropdown ? (
                              <>
                                <FormikSelectField
                                  name={`val-${group.id}-${condition.id}`}
                                  value={condition.value}
                                  isMulti={isMultiOperator(condition.operator)}
                                  disabled={isNullOperator(condition.operator)}
                                  onChange={(name, value, selectedLabel) =>
                                    handleConditionChange(
                                      group.id,
                                      condition.id,
                                      "value",
                                      value,
                                      selectedLabel
                                    )
                                  }
                                >
                                  {/* {(
                                    apiOptionsCache.current.get(
                                      selectedField.apiEndpoint
                                    ) || [{ label: "Select Value", value: "" }]
                                  ).map((opt) => (
                                    <option
                                      key={`${
                                        selectedField.apiEndpoint
                                      }-${String(opt.value)}`}
                                      value={opt.value}
                                    >
                                      {opt.label}
                                    </option>
                                  ))} */}
                                  {apiOptions
  .filter((opt) => {
    const isSpecial = selectedField.apiEndpoint.includes("/store") ||
                      selectedField.apiEndpoint.includes("/market") ||
                      selectedField.apiEndpoint.includes("/region") ||
                      selectedField.apiEndpoint.includes("/company");

    if (!isSpecial && opt.hasOwnProperty("active")) {
      return opt.active === "0" || getOptionValue(opt) === condition.value;
    }
    return true;
  })
  .map((opt) => {
      let label = "";
      if (selectedField.apiEndpoint.includes("/store")) {
        label = `${opt.store_name}${opt.sap ? `, ${opt.sap}` : ""}`;
      } else if (selectedField.apiEndpoint.includes("/market")) {
        label = opt.market_name || `Option ${getOptionValue(opt)}`;
      } else if (selectedField.apiEndpoint.includes("/region")) {
        label = opt.region_name || `Option ${getOptionValue(opt)}`;
      } else if (selectedField.apiEndpoint.includes("/company")) {
        label = opt.company_name || opt.label || `Option ${getOptionValue(opt)}`;
      } else {
        label = opt.label || `Option ${getOptionValue(opt)}`;
      }

    return (
      <option
        key={`${selectedField.apiEndpoint}-${getOptionValue(opt)}`}
        value={getOptionValue(opt)}
      >
        {label}
      </option>
    );
  })}

                                </FormikSelectField>
                                {!isNullOperator(condition.operator) &&
                                  renderError(group.id, condition.id, "value")}
                              </>
                            ) : selectedField?.type === "boolean" ? (
                              <>
                                <Form.Select
                                  value={String(condition.value)}
                                  onChange={(e) => {
                                    return handleConditionChange(
                                      group.id,
                                      condition.id,
                                      "value",
                                      e.target.value,
                                      e.target.value === "true" ? "Yes" : "No"
                                    );
                                  }}
                                  onBlur={() =>
                                    markTouched(group.id, condition.id, "value")
                                  }
                                >
                                  <option value="true">Yes</option>
                                  <option value="false">No</option>
                                </Form.Select>
                                {/* {renderError(group.id, condition.id, "value")} */}
                              </>
                            ) : selectedField?.type === "date" ? (
                              <>
                                <Form.Control
                                  type="date"
                                  value={condition.value}
                                  onChange={(e) =>
                                    handleConditionChange(
                                      group.id,
                                      condition.id,
                                      "value",
                                      e.target.value,
                                      e.target.value
                                    )
                                  }
                                  onBlur={() =>
                                    markTouched(group.id, condition.id, "value")
                                  }
                                />
                                {renderError(group.id, condition.id, "value")}
                              </>
                            ) : selectedField?.type === "datetime" ? (
                              <>
                                <Form.Control
                                  type="datetime-local"
                                  value={condition.value}
                                  onChange={(e) =>
                                    handleConditionChange(
                                      group.id,
                                      condition.id,
                                      "value",
                                      e.target.value,
                                      e.target.value
                                    )
                                  }
                                  onBlur={() =>
                                    markTouched(group.id, condition.id, "value")
                                  }
                                />
                                {renderError(group.id, condition.id, "value")}
                              </>
                            ) : (
                              <>
                                <Form.Control
                                  type="text"
                                  placeholder="Enter value"
                                  value={condition.value}
                                  onChange={(e) =>
                                    handleConditionChange(
                                      group.id,
                                      condition.id,
                                      "value",
                                      e.target.value,
                                      e.target.value
                                    )
                                  }
                                  onBlur={() =>
                                    markTouched(group.id, condition.id, "value")
                                  }
                                />
                                {renderError(group.id, condition.id, "value")}
                              </>
                            )}
                          </Col>

                          <Col xs="auto">
                            {group.conditions.length > 1 && (
                              <Button
                                variant="light"
                                size="sm"
                                className="text-danger"
                                onClick={() =>
                                  handleRemoveCondition(group.id, condition.id)
                                }
                              >
                                🗑️
                              </Button>
                            )}
                          </Col>
                        </Row>
                      );
                    })}
                  </Card.Body>
                </Card>
                {/* </div> */}
              </div>
            ))
          )
        ) : null}
      </Card.Body>
    </Card>
    // </div>
  );
});

export default ConditionBuilder;
